import React, { useEffect, useRef, useState } from 'react'
import { useParams } from 'react-router-dom'
import { io } from 'socket.io-client'
import Navbar from '../Component/Navbar'
import axiosInstance from '../Api/axiosInstance'

const SOCKET_URL = process.env.REACT_APP_SOCKET_URL
const SOCKET_NS  = '/enterprise-admin'

const OL      = '#6b7c3e'
const OL_DARK = '#3d4a22'
const OL_PALE = '#f0f4e8'

const tripStopStatuses = {
  pending:    1,
  inProgress: 2,
  completed:  3,
  cancelled:  4,
}

const stopStatusLabel = {
  1: { label: 'Pending',     color: '#f59e0b', bg: '#fffbeb' },
  2: { label: 'In Progress', color: '#3b82f6', bg: '#eff6ff' },
  3: { label: 'Completed',   color: '#22c55e', bg: '#f0fdf4' },
  4: { label: 'Cancelled',   color: '#ef4444', bg: '#fff1f2' },
}

const TripTracking = () => {
  const { tripId } = useParams()

  const socketRef      = useRef(null)
  const mapRef         = useRef(null)
  const mapInstanceRef = useRef(null)
  const markerRef      = useRef(null)
  const stopMarkersRef = useRef([])
  const polylineRef    = useRef(null)

  const [connected,   setConnected]   = useState(false)
  const [driverInfo,  setDriverInfo]  = useState(null)
  const [trip,        setTrip]        = useState(null)
  const [loading,     setLoading]     = useState(true)
  const [error,       setError]       = useState('')
  const [lastUpdated, setLastUpdated] = useState(null)

  // ── Load Leaflet ──────────────────────────────────────────────────────────
  useEffect(() => {
    if (!document.getElementById('leaflet-css')) {
      const link   = document.createElement('link')
      link.id      = 'leaflet-css'
      link.rel     = 'stylesheet'
      link.href    = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css'
      document.head.appendChild(link)
    }

    const initMap = () => {
      if (!mapRef.current || mapInstanceRef.current) return
      const L   = window.L
      const map = L.map(mapRef.current).setView([20.5937, 78.9629], 5)
      L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        attribution: '© OpenStreetMap contributors',
      }).addTo(map)
      mapInstanceRef.current = map
    }

    if (window.L) {
      initMap()
    } else {
      const script  = document.createElement('script')
      script.src    = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.js'
      script.onload = initMap
      document.body.appendChild(script)
    }

    return () => {
      if (mapInstanceRef.current) {
        mapInstanceRef.current.remove()
        mapInstanceRef.current = null
      }
    }
  }, [])

  // ── Fetch trip details ────────────────────────────────────────────────────
  useEffect(() => {
    const fetchTrip = async () => {
      setLoading(true)
      setError('')
      try {
        const res = await axiosInstance.get(
          `/enterprise/trip/details/${tripId}`,
          { useAuth: true }
        )
        setTrip(res.data?.data || null)
      } catch (err) {
        console.error('Failed to fetch trip:', err)
        setError('Failed to load trip details. Please try again.')
        setTrip(null)
      } finally {
        setLoading(false)
      }
    }
    if (tripId) fetchTrip()
  }, [tripId])

  // ── Plot stops on map ─────────────────────────────────────────────────────
  useEffect(() => {
    if (!trip?.stops?.length) return

    // Defer to ensure map is fully sized
    setTimeout(tryPlot, 200)

    function tryPlot() {
      const L   = window.L
      const map = mapInstanceRef.current
      if (!L || !map) {
        setTimeout(tryPlot, 300)
        return
      }

      // Clear old markers & polyline
      stopMarkersRef.current.forEach(m => map.removeLayer(m))
      stopMarkersRef.current = []
      if (polylineRef.current) {
        map.removeLayer(polylineRef.current)
        polylineRef.current = null
      }

      const stops      = trip.stops
      const totalStops = stops.length
      const latlngs    = []

      stops.forEach((stop, idx) => {
        const isFirst = idx === 0
        const isLast  = idx === totalStops - 1
        const pos     = [stop.latitude, stop.longitude]
        latlngs.push(pos)

        let emoji  = '📦'
        let border = '#f59e0b'
        let bg     = '#fffbeb'

        if (isFirst) {
          emoji  = '🟢'
          border = '#22c55e'
          bg     = '#f0fdf4'
        } else if (isLast) {
          emoji  = '🏁'
          border = '#ef4444'
          bg     = '#fff1f2'
        }

        if (stop.status === tripStopStatuses.completed) {
          emoji  = '✅'
          border = '#22c55e'
          bg     = '#f0fdf4'
        } else if (stop.status === tripStopStatuses.inProgress) {
          emoji  = '🔵'
          border = '#3b82f6'
          bg     = '#eff6ff'
        }

        const label = isFirst ? 'Pickup' : isLast ? 'Drop' : `Stop ${idx}`

        const icon = L.divIcon({
          className: '',
          html: `<div style="
            background:${bg};
            border:2.5px solid ${border};
            border-radius:50%;
            width:34px;
            height:34px;
            display:flex;
            align-items:center;
            justify-content:center;
            font-size:16px;
            box-shadow:0 2px 8px rgba(0,0,0,0.18);
            position:relative;
          ">
            ${emoji}
            <div style="
              position:absolute;
              bottom:-18px;
              left:50%;
              transform:translateX(-50%);
              background:${border};
              color:#fff;
              font-size:9px;
              font-weight:700;
              padding:1px 5px;
              border-radius:4px;
              white-space:nowrap;
              font-family:DM Sans,sans-serif;
            ">${label}</div>
          </div>`,
          iconSize:   [34, 34],
          iconAnchor: [17, 17],
          popupAnchor:[0, -20],
        })

        const marker = L.marker(pos, { icon })
          .addTo(map)
          .bindPopup(`
            <div style="font-family:'DM Sans',sans-serif;min-width:160px">
              <b style="font-size:13px">${label}: ${stop.name}</b><br/>
              <span style="font-size:11px;color:#555">${stop.address}</span><br/>
              <span style="font-size:11px">👤 ${stop.contactName} · ${stop.contactPhone}</span><br/>
              <span style="
                display:inline-block;margin-top:4px;
                padding:1px 8px;border-radius:5px;font-size:11px;font-weight:600;
                background:${stopStatusLabel[stop.status]?.bg};
                color:${stopStatusLabel[stop.status]?.color};
              ">${stopStatusLabel[stop.status]?.label || 'Unknown'}</span>
            </div>
          `)

        stopMarkersRef.current.push(marker)
      })

      // Draw polyline + fitBounds deferred so map pixel size is ready
      map.invalidateSize()
      setTimeout(() => {
        if (!mapInstanceRef.current) return
        try {
          if (latlngs.length >= 2) {
            polylineRef.current = L.polyline(latlngs, {
              color:     OL,
              weight:    3,
              dashArray: '8, 8',
              opacity:   0.75,
            }).addTo(map)
          }

          const bounds = L.latLngBounds(latlngs)
          if (bounds.isValid()) {
            map.fitBounds(bounds, { padding: [60, 60] })
          }
        } catch (e) {
          console.warn('Polyline/bounds error:', e)
        }
      }, 100)
    }
  }, [trip])

  // ── Socket.IO ─────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!tripId) return

    const token = localStorage.getItem('token') || localStorage.getItem('authToken') || ''

    const socket = io(`${SOCKET_URL}${SOCKET_NS}`, {
      transports: ['polling', 'websocket'],
      reconnection: true,
      reconnectionAttempts: 10,
      reconnectionDelay: 2000,
      timeout: 20000,
      auth: { token },
    })
    socketRef.current = socket

    socket.on('connect', () => {
      setConnected(true)
      setError('')
      socket.emit('enterpriseAdminTripTracking', { tripId })
    })

    socket.on('reconnect', () => {
      socket.emit('enterpriseAdminTripTracking', { tripId })
    })

    socket.on('disconnect', (reason) => {
      setConnected(false)
      if (reason === 'io server disconnect') socket.connect()
    })

    socket.on('connect_error', (err) => {
      setError('Connection failed: ' + err.message)
      setConnected(false)
    })

    socket.on('enterpriseDriverLocationUpdate', (data) => {
      const { tripId: tid, driverId, lat, lng } = data
      setDriverInfo({ tripId: tid, driverId, lat, lng })
      setLastUpdated(new Date().toLocaleTimeString())
      updateMarker(lat, lng)
    })

    return () => { socket.disconnect() }
  }, [tripId])

  // ── Moving truck marker ───────────────────────────────────────────────────
  const updateMarker = (lat, lng) => {
    const L   = window.L
    const map = mapInstanceRef.current
    if (!L || !map) return

    const position = [lat, lng]

    if (markerRef.current) {
      markerRef.current.setLatLng(position)
    } else {
      const icon = L.divIcon({
        className: '',
        html: `<div style="
          width:36px;height:36px;border-radius:50%;
          background:${OL};border:3px solid #fff;
          box-shadow:0 2px 8px rgba(0,0,0,0.3);
          display:flex;align-items:center;justify-content:center;
          font-size:18px;
        ">🚚</div>`,
        iconSize:   [36, 36],
        iconAnchor: [18, 18],
      })
      markerRef.current = L.marker(position, { icon }).addTo(map)
    }

    map.flyTo(position, 15, { animate: true, duration: 1 })
  }

  const pickup = trip?.stops?.[0]
  const drop   = trip?.stops?.[trip?.stops?.length - 1]

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700&display=swap');
        * { box-sizing: border-box; margin: 0; padding: 0; }
        body { font-family: 'DM Sans', sans-serif; }

        .pulse {
          display: inline-block; width: 10px; height: 10px;
          border-radius: 50%; background: #22c55e;
          box-shadow: 0 0 0 0 rgba(34,197,94,0.4);
          animation: pulse 1.5s infinite;
        }
        .pulse.off {
          background: #ef4444;
          box-shadow: 0 0 0 0 rgba(239,68,68,0.4);
          animation: none;
        }
        @keyframes pulse {
          0%   { box-shadow: 0 0 0 0   rgba(34,197,94,0.4); }
          70%  { box-shadow: 0 0 0 8px rgba(34,197,94,0);   }
          100% { box-shadow: 0 0 0 0   rgba(34,197,94,0);   }
        }
        @keyframes spin { to { transform: rotate(360deg); } }

        .stop-row { transition: background 0.15s; }
        .stop-row:hover { background: ${OL_PALE} !important; border-radius: 8px; }

        .panel-scroll::-webkit-scrollbar { width: 4px; }
        .panel-scroll::-webkit-scrollbar-track { background: transparent; }
        .panel-scroll::-webkit-scrollbar-thumb { background: #d1d5db; border-radius: 4px; }
      `}</style>

      <Navbar />
      <div style={S.page}>

        {/* ── Header ── */}
        <div style={S.header}>
          <div style={S.headerLeft}>
            <span style={S.headerIcon}>🚚</span>
            <div>
              <h1 style={S.title}>Live Trip Tracking</h1>
              <p style={S.tripIdText}>
                Trip ID: <span style={S.tripIdBadge}>{tripId}</span>
              </p>
            </div>
          </div>
          <div style={S.statusPill}>
            <span className={connected ? 'pulse' : 'pulse off'} />
            <span style={{
              fontSize: 13, fontWeight: 600,
              color: connected ? '#15803d' : '#b91c1c',
            }}>
              {connected ? 'Connected' : 'Disconnected'}
            </span>
          </div>
        </div>

        {error && <div style={S.errorBox}>⚠️ {error}</div>}

        <div style={S.body}>

          {/* ── Map ── */}
          <div style={S.mapWrapper}>
            <div ref={mapRef} style={S.map} />
            {loading && (
              <div style={S.mapOverlay}>
                <div style={S.spinner} />
                <p style={{ marginTop: 12, color: '#6b7280', fontSize: 14 }}>
                  Loading trip data…
                </p>
              </div>
            )}
          </div>

          {/* ── Side Panel ── */}
          <div style={S.panel} className="panel-scroll">

            {/* Route card */}
            {pickup && (
              <div style={S.card}>
                <h2 style={S.cardTitle}>📍 Route</h2>

                {trip.stops.map((stop, idx) => {
                  const isFirst = idx === 0
                  const isLast  = idx === trip.stops.length - 1
                  const label   = isFirst ? 'Pickup' : isLast ? 'Drop' : `Stop ${idx}`
                  const dotColor = isFirst ? '#22c55e' : isLast ? '#ef4444' : '#f59e0b'
                  const dotEmoji = isFirst ? '🟢' : isLast ? '🏁' : '📦'

                  return (
                    <div
                      key={stop._id}
                      className="stop-row"
                      style={{
                        ...S.stopRow,
                        borderBottom: idx < trip.stops.length - 1
                          ? '1px dashed #e5e7eb' : 'none',
                      }}
                      onClick={() => {
                        const map = mapInstanceRef.current
                        if (map) map.flyTo([stop.latitude, stop.longitude], 16, { duration: 1 })
                      }}
                    >
                      {/* Left dot + connector line */}
                      <div style={S.stopDotCol}>
                        <div style={{ ...S.stopDot, background: dotColor }}>
                          {dotEmoji}
                        </div>
                        {idx < trip.stops.length - 1 && (
                          <div style={S.connector} />
                        )}
                      </div>

                      {/* Stop details */}
                      <div style={S.stopBody}>
                        <div style={S.stopLabelRow}>
                          <span style={S.stopLabel}>{label}</span>
                          <StopBadge status={stop.status} />
                        </div>
                        <div style={S.stopName}>{stop.name}</div>
                        <div style={S.stopAddr}>{stop.address}</div>
                        <div style={S.stopContact}>
                          👤 {stop.contactName} · {stop.contactPhone}
                        </div>
                      </div>
                    </div>
                  )
                })}
              </div>
            )}

            {/* Driver live info */}
            {/* <div style={S.card}>
              <h2 style={S.cardTitle}>🚚 Driver Location</h2>

              {driverInfo ? (
                <>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                    <InfoRow label="Trip ID"   value={driverInfo.tripId} />
                    <InfoRow label="Driver ID" value={driverInfo.driverId} />
                    <InfoRow label="Latitude"  value={Number(driverInfo.lat).toFixed(6)} />
                    <InfoRow label="Longitude" value={Number(driverInfo.lng).toFixed(6)} />
                    <InfoRow
                      label="Updated"
                      value={lastUpdated}
                      valueStyle={{ color: OL, fontWeight: 700 }}
                    />
                  </div>
                  <div style={S.coordBox}>
                    <span style={S.coordLabel}>📍 Current Position</span>
                    <span style={S.coordValue}>
                      {Number(driverInfo.lat).toFixed(5)},&nbsp;
                      {Number(driverInfo.lng).toFixed(5)}
                    </span>
                  </div>
                </>
              ) : (
                <div style={S.waitingBox}>
                  <div style={S.spinner} />
                  <p style={{ color: '#9ca3af', fontSize: 13, marginTop: 10 }}>
                    Waiting for driver to connect…
                  </p>
                </div>
              )}
            </div> */}

          </div>
        </div>
      </div>
    </>
  )
}

// ── Helper components ─────────────────────────────────────────────────────────
const InfoRow = ({ label, value, valueStyle = {} }) => (
  <div style={S.infoRow}>
    <span style={S.infoLabel}>{label}</span>
    <span style={{ ...S.infoValue, ...valueStyle }}>{value}</span>
  </div>
)

const StopBadge = ({ status }) => {
  const s = stopStatusLabel[status] || { label: 'Unknown', color: '#888', bg: '#f3f4f6' }
  return (
    <span style={{
      fontSize: 10, fontWeight: 700,
      padding: '2px 7px', borderRadius: 5,
      background: s.bg, color: s.color,
      whiteSpace: 'nowrap',
    }}>
      {s.label}
    </span>
  )
}

export default TripTracking

// ── Styles ────────────────────────────────────────────────────────────────────
const S = {
  page: {
    minHeight: '100vh', background: '#f7f8f4',
    fontFamily: "'DM Sans', sans-serif",
    display: 'flex', flexDirection: 'column',
  },
  header: {
    background: '#fff', borderBottom: '1px solid #e0e5d4',
    padding: '16px 28px',
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    boxShadow: '0 2px 8px rgba(107,124,62,0.07)',
    position: 'sticky', top: 0, zIndex: 100,
  },
  headerLeft:  { display: 'flex', alignItems: 'center', gap: 14 },
  headerIcon:  { fontSize: 28 },
  title:       { fontSize: 20, fontWeight: 700, color: OL_DARK, letterSpacing: '-0.3px' },
  tripIdText:  { fontSize: 12, color: '#9ca3af', marginTop: 2 },
  tripIdBadge: {
    background: OL_PALE, color: OL_DARK, borderRadius: 5,
    padding: '1px 8px', fontSize: 12, fontWeight: 700,
    fontFamily: 'monospace', border: `1px solid ${OL}30`,
  },
  statusPill: {
    display: 'flex', alignItems: 'center', gap: 7,
    background: '#f9fafb', border: '1.5px solid #e5e7eb',
    borderRadius: 20, padding: '6px 14px',
  },
  errorBox: {
    margin: '12px 28px 0', padding: '10px 16px',
    background: '#fff1f2', border: '1.5px solid #fca5a5',
    borderRadius: 8, color: '#b91c1c', fontSize: 13, fontWeight: 500,
  },
  body: {
    display: 'flex', flex: 1, gap: 20,
    padding: 24, alignItems: 'flex-start',
  },

  // Map
  mapWrapper: {
    flex: 1, borderRadius: 16, overflow: 'hidden',
    border: '1px solid #e2e8d4',
    boxShadow: '0 2px 16px rgba(107,124,62,0.09)',
    position: 'relative', minHeight: 500, background: '#e8eedd',
  },
  map: { width: '100%', height: 'calc(100vh - 130px)', minHeight: 500 },
  mapOverlay: {
    position: 'absolute', inset: 0, zIndex: 999,
    display: 'flex', flexDirection: 'column',
    alignItems: 'center', justifyContent: 'center',
    background: 'rgba(240,244,232,0.88)',
    pointerEvents: 'none',
  },

  // Panel
  panel: {
    width: 320, flexShrink: 0,
    display: 'flex', flexDirection: 'column', gap: 14,
    maxHeight: 'calc(100vh - 110px)', overflowY: 'auto',
    paddingRight: 2,
  },

  // Card
  card: {
    background: '#fff', borderRadius: 14,
    border: '1px solid #e2e8d4',
    padding: '16px 18px',
    boxShadow: '0 2px 8px rgba(107,124,62,0.06)',
    display: 'flex', flexDirection: 'column', gap: 12,
  },
  cardTitle: { fontSize: 15, fontWeight: 700, color: OL_DARK },

  // Stop row
  stopRow: {
    display: 'flex', gap: 10, padding: '10px 4px',
    cursor: 'pointer',
  },
  stopDotCol: {
    display: 'flex', flexDirection: 'column',
    alignItems: 'center', flexShrink: 0,
  },
  stopDot: {
    width: 28, height: 28, borderRadius: '50%',
    display: 'flex', alignItems: 'center',
    justifyContent: 'center', fontSize: 13, flexShrink: 0,
  },
  connector: {
    width: 2, flex: 1, minHeight: 12,
    background: '#e5e7eb', margin: '3px 0',
  },
  stopBody:    { flex: 1, minWidth: 0 },
  stopLabelRow:{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 2 },
  stopLabel:   { fontSize: 10, fontWeight: 700, color: '#9ca3af', letterSpacing: '0.07em', textTransform: 'uppercase' },
  stopName:    { fontSize: 13, fontWeight: 700, color: OL_DARK },
  stopAddr:    { fontSize: 11, color: '#6b7280', marginTop: 2, lineHeight: 1.4 },
  stopContact: { fontSize: 11, color: '#9ca3af', marginTop: 3 },

  // Info rows
  infoRow:   { display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
  infoLabel: { fontSize: 12, color: '#9ca3af', fontWeight: 500 },
  infoValue: { fontSize: 13, fontWeight: 600, color: '#1f2937' },

  // Coord box
  coordBox: {
    background: OL_PALE, borderRadius: 10,
    border: `1px solid ${OL}25`, padding: '10px 14px',
    display: 'flex', flexDirection: 'column', gap: 3,
  },
  coordLabel: { fontSize: 10, color: OL, fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase' },
  coordValue: { fontSize: 13, fontWeight: 700, color: OL_DARK, fontFamily: 'monospace' },

  // Waiting / spinner
  waitingBox: {
    display: 'flex', flexDirection: 'column',
    alignItems: 'center', padding: '20px 0',
  },
  spinner: {
    width: 26, height: 26,
    border: `3px solid ${OL_PALE}`, borderTopColor: OL,
    borderRadius: '50%', animation: 'spin 0.8s linear infinite',
  },

  // Connection details
  detailsTitle: {
    fontSize: 11, fontWeight: 700, color: '#9ca3af',
    letterSpacing: '0.07em', textTransform: 'uppercase',
  },
}